import { DrawLimitAlertsComponent } from "./components/draw-limit-alerts.component";
import { HistoricalChartButtonComponent } from "./components/historical-chart-button.component";
import { IndicatorLastPriceComponent } from "./components/indicators-last-price.component";
import { LastPriceComponent } from "./components/last-price.component";
import { TiChartSeriesComponent } from "./components/ti-chart-series.component";
import { TILogoComponent } from "./components/ti-logo.component";
import { BaseChartContainer } from "./models/base/base-chart.container";
import { IChartContainer } from "./models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "./models/chart-style/chart-style-settings.model";
import { Timeframe } from "./models/enum/timeframe.enum";
import { WindowFormPnPChartDataService } from "./services/window-form-pnp-chart-data.service";
import { TiChartMenuContainer } from "./ti-chart-menu.container";

export class TiChartPnpContainer extends BaseChartContainer {

    private tiChartMenu: TiChartMenuContainer | null = null;

    public baseChart: IChartContainer;

    private pnpChartStyleInited = false;
    private pnpSwitchElement: HTMLElement | null = null;

    constructor(baseChart: IChartContainer) {
        super();

        this.baseChart = baseChart;
        this.element = document.body.findFirst('.ti-pro-pnp');
        if (!this.element) {
            return;
        }

        this.pnpMode = true;
        this.chartService = new WindowFormPnPChartDataService(this);
        
        // this.drawingToolsManager = new DrawingToolsManager(this);

        const chartMenuElement = this.element.findFirst('.chart-menu-box');
        if (chartMenuElement == null) {
            console.error('ERROR: .chart-menu-box element not found, html needs to be udpated')
            return;
        }

        //Note: the menu needs to be created before the chart series, since HighChart Indicators calls IndicatorLastPrice
        this.tiChartMenu = new TiChartMenuContainer(chartMenuElement, this);

        this.setPnPSize();

        this.pnpSwitchElement = baseChart.getPnPSwitchElement();
        this.chartService.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            if (!this.pnpChartStyleInited && chartStyle.pnpInitialed) {
                // console.log(' subscribeChartStyleData !!!', chartStyle);
                this.pnpChartStyleInited = true;
                this.setChartStylePosition(chartStyle);
            } else if (!this.pnpChartStyleInited && !chartStyle.pnpEnabled) {
                this.pnpChartStyleInited = true;
                this.element.addClass('hidden');
                this.pnpSwitchElement?.addClass('hidden');
            }
        });

        this.subscribeChartRequestingNewSymbol();
        this.createTIChartSeriesComponent();
        this.createIndicatorsLastPriceComponent();
        this.createLastPriceComponent();
        this.createTILogoComponent();
        this.createLimitAlertsComponent();
        this.createHistoricalChartButtonComponent();
        this.setPnPLeftPosition();
    }

    private createTIChartSeriesComponent() {
        this.tiChartSeries = new TiChartSeriesComponent(this.element, this);
        this.components.push(this.tiChartSeries);
    }

    private createLastPriceComponent() {
        this.element.querySelectorAll<HTMLElement>('.main-symbol-last-price').forEach((el: HTMLElement) => {
            this.components.push(new LastPriceComponent(el, this));
        });
    }

    private createIndicatorsLastPriceComponent() {
        this.element.querySelectorAll<HTMLElement>('.indicator-last-prices').forEach((el: HTMLElement) => {
            this.components.push(new IndicatorLastPriceComponent(el, this));
        });
    }

    private createTILogoComponent() {
        this.element.querySelectorAll<HTMLElement>('.logo-wrapper').forEach((el: HTMLElement) => {
            this.components.push(new TILogoComponent(el, this));
        });
    }

    private createLimitAlertsComponent() {
        this.components.push(new DrawLimitAlertsComponent(this));
    }
    
    private createHistoricalChartButtonComponent() {
        this.element.querySelectorAll<HTMLElement>('.historical-chart-button').forEach((el: HTMLElement) => {
            this.components.push(new HistoricalChartButtonComponent(el, this));
        });
    }

    private setChartStylePosition(chartStyle: ChartStyleSettingsModel) {
        const positionSplits = chartStyle.pnpPosition.split('_');
        if (positionSplits?.length >= 2) {
            let isTop = false;
            if (positionSplits[0]) {
                this.element.addClass(positionSplits[0]);
            }
            if (positionSplits[1]) {
                isTop = positionSplits[1] == 'true';
            }

            if (isTop) {
                this.element.style.top = '75px';
            } else {
                this.element.style.top = 'calc(100% - ' + this.element.style.height + ')';
            }
        } else {
            this.element.style.top = 'calc(100% - ' + this.element.style.height + ')';
        }

        if (!chartStyle.pnpExpanded) {
            this.element.removeClass('show');
            this.alreadyCickedCollapse = true;
        } else {
            this.element.addClass('show');
        }
        
        this.onArrowOnOffClick();
        this.onResize();
        this.onDblClick();
        this.onDrag();
        this.subscribeChartComponentWithHighChartPopup();
        this.setPnPLeftPosition();
        this.element.removeClass('visually-hidden');
    }

    private subscribeChartComponentWithHighChartPopup() {
        this.baseChart.tiChartSeries?.subscribePopupChange((open: boolean, event: any) => {
            const popup = document.body.findFirst('.highcharts-popup:not(.custom-highchart-popup)');

            if (open) {
                if (!popup) {
                    return;
                }

                if (this.element.classList.contains('show')) {
                    this.element.findFirst('.pnp-on-off-arrow .arrow')?.click();
                }
            }
        });
    }

    private alreadyCickedCollapse = false;
    private onArrowOnOffClick() {
        const arrows = this.element.querySelectorAll<HTMLElement>('.pnp-on-off-arrow .arrow');
        arrows.forEach((el: HTMLElement) => {
            el.addEventListener('click', (e: MouseEvent) => {
                e.preventDefault();

                this.element.style.transition = 'left ease-in-out 1s';
                arrows.forEach((a: HTMLElement) => {
                    a.addClass('hidden');
                });

                this.element.classList.toggle('show');
                if (!this.element.classList.contains('show')) {
                    this.alreadyCickedCollapse = true;
                }

                this.setPnPLeftPosition();
                this.requestSavePnPState();

                setTimeout(() => {
                    this.element.style.transition = '';
                    arrows.forEach((a: HTMLElement) => {
                        a.removeClass('hidden');
                    });
                }, 1000);
            });
        });
    }

    private setPnPLeftPosition() {
        if (this.element.classList.contains('right')) {
            if (!this.element.classList.contains('show')) {
                this.element.style.left = '100%';
            } else {
                this.element.style.left = `calc(100% - ${this.element.style.width})`;
            }
        } else {
            if (!this.element.classList.contains('show')) {
                const width = parseInt(this.element.style.width.replace('px', ''), 10);
                this.element.style.left = '-' + width + 'px';
            } else {
                let extraLeft = 0;
                if (this.element.containClass('level1')) {
                    extraLeft = 52;
                } else if (this.element.containClass('level2')) {
                    extraLeft = 92;;
                }

                this.element.style.left = extraLeft + 'px';
            }
        }
    }

    private setPnPSize() {
        const baseChart = this.baseChart as any;
        const mainChartElementWidth = baseChart.element.clientWidth;
        const mainChartElementHeight = baseChart.element.clientHeight;

        let width = mainChartElementWidth / 3;
        let height = mainChartElementHeight / 3;

        if (width < 210) {
            width = 210;
        } 

        if (width <= 210 || this.element.containClass('right') && width <= 300) {
            this.element.addClass('min-tf');
        } else {
            this.element.removeClass('min-tf');
        }

        if (mainChartElementWidth <= 470) {
            this.element.addClass('small-logo');
        } else {
            this.element.removeClass('small-logo');
        }

        if (height < 140) {
            height = 140;
        }

        const newHeight = height + 'px';
        this.element.style.width = width + 'px';
        this.element.style.height = newHeight;
        
        if (this.element.style.top != '75px') {
            this.element.style.top = 'calc(100% - ' + newHeight + ')';
        }


        // Auto Collapse PIP on small chart:
        // Make the PIP minimum width 210. If the Main chart width goes below 2x that width (420), then collapse the PIP
        // Make the PIP minimum height 140. If the Main chart height goes below 2x that height (280), then collapse the PIP
        const widowWidth = this.body.clientWidth;
        const windowHeight = this.body.clientHeight;

        if (widowWidth >= 420 && windowHeight >= 280) {
            if (!this.alreadyCickedCollapse) {
                this.element.addClass('show');
            }
            this.pnpSwitchElement?.display();
        } else {
            this.element.removeClass('show');
            this.pnpSwitchElement?.hide();
        }

        this.setPnPLeftPosition();

        setTimeout(() => {
            this.tiChartSeries?.highStockChart?.reflow();
        }, 320);
    }

    private onDblClick() {
        const baseChart = this.baseChart as any;
        this.element.addEventListener('dblclick', (e: MouseEvent) => {
            const targetElement = e.target as HTMLElement;
            if (targetElement.closest('.time')) {
                return;
            }

            baseChart.pnpSwitchComponent?.togglePnPMode();
            baseChart.pnpSwitchComponent?.requestTimeFrame();
        });

        // baseChart.element.addEventListener('dblclick', (e: MouseEvent) => {
        //     const targetElement = e.target as HTMLElement;
        //     if (targetElement.closest('.time') || targetElement.classList.contains('highcharts-annotation') || targetElement.closest('.highcharts-annotation')) {
        //         return;
        //     }

        //     baseChart.pnpSwitchComponent?.requestTimeFrame();
        //     baseChart.pnpSwitchComponent?.togglePnPMode();
        // });
    }

    private onDrag() {
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
        let dragStart = false;

        this.element.addEventListener('mousedown', (e: MouseEvent) => {
            const targetElement = e.target as HTMLElement;
            if (dragStart || targetElement.classList.contains('arrow') || targetElement.closest('.time') || targetElement.closest('.pnp-on-off-arrow') || targetElement.closest('.highcharts-plot-line')) {
                return;
            }

            dragStart = true;

            pos3 = e.clientX;
            pos4 = e.clientY;

            this.element.addClass('dragging');
            this.tiChartMenu?.closeAllMenuControls();
        });

        this.body.addEventListener('mousemove', (e: MouseEvent) => {
            if (!dragStart) {
                return;
            }

            pos1 = pos3 - e.clientX;
            pos2 = pos4 - e.clientY;
            pos3 = e.clientX;
            pos4 = e.clientY;

            this.element.style.left = (this.element.offsetLeft - pos1) + 'px';
            this.element.style.top = (this.element.offsetTop - pos2) + 'px';
        });        
        
        this.body.addEventListener('mouseleave', () => {
            if (!dragStart) {
                return;
            }

            dragStart = false;
            this.setPnPPositionAfterDrag();
        });

        this.body.addEventListener('mouseup', () => {
            if (!dragStart) {
                return;
            }

            dragStart = false;
            this.setPnPPositionAfterDrag();
        });
    }

    private setPnPPositionAfterDrag() {
        this.element.removeClass('dragging');

        const baseChart = this.baseChart as any;

        const bodyWidth = baseChart.element.clientWidth;
        const bodyHeight = baseChart.element.clientHeight;

        this.element.style.transition = 'all ease-in-out 1s';
        setTimeout(() => {
            this.element.style.transition = '';
        });

        if (bodyWidth / 2 < this.element.offsetLeft) {
            this.element.addClass('right');
            if (bodyHeight / 2 < this.element.offsetTop) {
                this.element.style.left = `calc(100% - ${this.element.style.width})`;
                this.element.style.top = `calc(100% - ${this.element.style.height})`;
            } else {
                this.element.style.left = `calc(100% - ${this.element.style.width})`;
                this.element.style.top = `75px`;
            }
        } else {
            this.element.removeClass('right');
            let extraLeft = 0;
            if (this.element.containClass('level1')) {
                extraLeft = 52;
            } 
            
            if (this.element.containClass('level2')) {
                extraLeft = 92;;
            }

            if (bodyHeight / 2 < this.element.offsetTop) {
                this.element.style.left = extraLeft + 'px';
                this.element.style.top = `calc(100% - ${this.element.style.height})`;
            } else {
                this.element.style.left = extraLeft + 'px';
                this.element.style.top = `75px`;
            }
        }

        this.requestSavePnPState();
    }

    private requestSavePnPState() {
        const expanded = this.element.containClass('show');
        const className = this.element.containClass('right') ? 'right' : '';
        const top = this.element.style.top == '75px';
        const positionKey = className + '_' + top;
        this.chartService.requestSetPnPState(positionKey, expanded, this.tiChartSeries.currentTimeframe)
    }

    private subscribeChartRequestingNewSymbol() {
        this.chartService.subscribeChartRequestingNewSymbolData((symbol: string, timeframe: Timeframe) => {
            if (!this.tiChartSeries || this.tiChartSeries.symbol != symbol) {
                this.setComponentStateWhenRequestingNewSymbol(symbol, this.tiChartSeries.currentTimeframe);
            }
        });
    }

    public override setComponentStateWhenRequestingNewSymbol(symbol: string, timeframe: Timeframe) {
        const drawLimitAlertsComponent = this.getComponentsByType(DrawLimitAlertsComponent)[0] as DrawLimitAlertsComponent;
        drawLimitAlertsComponent?.remove();

        this.hideTooltip();
        
        if (this.tiChartSeries) {
            this.tiChartSeries.highChartComparisonBuilder?.removeComparisonsComponents(true);
            this.tiChartSeries.currentTimeframe = timeframe;
            this.tiChartSeries.symbol = symbol;
            this.tiChartSeries.setPanel();
            this.tiChartSeries.hideWithOpacity();
            this.tiChartSeries.highChartAxisBuilder?.reset();
        }
    }

    private resizeTimmer = null as any;
    private onResize() {
        this.domEvents.onResize(() => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                if (this.tiChartSeries.highStockChart) {
                    this.setPnPSize();
                    this.tiChartSeries.start();
                }
            }, 150);
        });
    }
}